<template>
  <form class="d-flex flex-grow-1 h-0 flex-column" @submit="onSubmit">
    <GmbControlColumn noMaxWidth>
      <GmbSection :displayLevel="1" height="parent" lockHeader :title="title">
        <template #picto>
          <slot name="picto"/>
        </template>

        <div class="d-flex mx-0 container-xl px-0 flex-column">
          <template v-if="formModel">
            <template
                v-if="isCreationMode || isSingleUser"
                ref="addressFormFields"
            >
              <template v-if="isCreationMode">
                <GmbRow>
                  <FdText tag="label">Site :</FdText>
                  <b-form-select
                      v-model="formModel.organization"
                      class="mt-1"
                      id="form-input-organization"
                      :options="organizationSelectOptions"
                  />
                </GmbRow>

                <GmbRow class="mt-2">
                  <FdText tag="label">Référence :</FdText>
                  <b-form-input
                      v-model="formModel.reference_id"
                      class="mt-1"
                  />
                </GmbRow>

                <GmbRow class="mt-2">
                  <FdText tag="label">Email :</FdText>
                  <b-form-input
                      v-model="formModel.individual_email"
                      type="email"
                      class="mt-1"
                  />
                </GmbRow>

              </template>

              <GmbRow class="mt-2">
                <FdText :class="{ 'mt-3': isCreationMode }" tag="label"
                >Rue :
                </FdText
                >
                <b-form-input
                    v-model="formModel.addressFormFields.address"
                    class="mt-1"
                />
              </GmbRow>

              <GmbRow class="mt-2">
                <FdText tag="label">Code postal :</FdText>
                <b-form-input
                    v-model="formModel.addressFormFields.postcode"
                    class="mt-1"
                />
              </GmbRow>

              <GmbRow class="mt-2">
                <FdText tag="label">Ville :</FdText>
                <b-form-input
                    v-model="formModel.addressFormFields.city"
                    class="mt-1"
                />
              </GmbRow>

              <GmbRow class="mt-2">
                <FdText tag="label">Longitude (optionnel) :</FdText>
                <b-form-input
                    v-model="formModel.addressFormFields.longitude"
                    class="mt-1"
                    placeholder="Exemple : 45.76141"
                    step="any"
                    type="number"
                />
              </GmbRow>

              <GmbRow class="mt-2">
                <FdText tag="label">Latitude (optionnel) :</FdText>
                <b-form-input
                    v-model="formModel.addressFormFields.latitude"
                    class="mt-1"
                    placeholder="Exemple : -10.71241"
                    step="any"
                    type="number"
                />
              </GmbRow>
            </template>

            <!-- This part of the form, works for BOTH SINGLE AND MULTIPLE individual(s) edition -->
            <template v-for="(filter, i) in this.filtersForForm">
              <GmbRow :key="i" class="mt-2">
                <FdText tag="label">{{ filter.label }} :</FdText>
                <b-form-select
                    v-model="formModel.filterFormFields[filter.slug]"
                    :id="'form-input-filter-' + i"
                    class="mt-1"
                    :key="i + '-select-option'"
                    :options="filter.filterOptions"
                />
              </GmbRow>
            </template>
          </template>

          <GmbRow v-else>
            <FdText tag="span">Aucune donnée</FdText>
          </GmbRow>
        </div>
      </GmbSection>

      <div></div>
      <!--- DO NOT remove this, it solves a weird bug with template in Vue.js 2/v-if/slots, for controls below -->

      <template #controls>
        <div
            class="row no-gutters"
            v-if="(individuals && individuals.length) || isCreationMode"
        >
          <div class="d-flex col flex-column align-items-start">
            <b-button
                class="d-flex py-1 px-4 justify-content-center align-items-center text-size-reset rounded-1"
                type="submit"
                variant="primary"
            >Valider
            </b-button
            >
          </div>

          <div
              v-if="individuals && individuals.length"
              class="d-flex col-auto pl-3 flex-column"
          >
            <b-button
                class="d-flex p-1 justify-content-center align-items-center text-size-reset rounded-1 btn-transparent-danger"
                type="button"
                @click.prevent="deleteIndividuals(individuals)"
            >
              <FdText fa><i class="fas fa-close"/></FdText>
              <FdText class="mx-2" tag="span">Supprimer</FdText>
            </b-button>
          </div>

          <div v-if="hasControl" class="d-flex col-auto pl-3 flex-column">
            <slot name="control"/>
          </div>
        </div>
      </template>
    </GmbControlColumn>
  </form>
</template>

<script>
import {hasEnrichedSlot, openToast} from "@/helpers.js";
import ModalEventBus from "@/modalEventBus.js";
import {patchApiEndpoint, postApiEndpoint} from "@/services/base";

import FdText from "@/flexdesign/Text";

import GmbControlColumn from "@/widgets/ControlColumn";
import GmbRow from "@/widgets/Row";
import GmbSection from "@/widgets/Section";

export default {
  name: "GmbDataEditionEditForm",

  components: {
    FdText,
    GmbControlColumn,
    GmbRow,
    GmbSection,
  },

  props: {
    filterTree: Object,
    individuals: Array,
    isCreationMode: Boolean,
    organizations: Array,
    title: String,
  },

  data() {
    return {
      formModel: null,
      indeterminateFilterOptionPlaceholderLabel: "Conserver la valeur",
      indeterminateFilterOptionPlaceholderSlug: "__INDETERMINATE__",
      isSingleUser: false,
    };
  },

  computed: {
    filtersForForm() {
      /* We have to adapt the filterTree input so that form fields find their config */
      if (!this.filterTree) return [];

      let filtersForForm = [];

      for (const filter of this.filterTree.filters) {
        let filterForForm = {
          filterOptions: filter.filterOptions.map((filterOption) => {
            return {
              /* Normalize names for form Select options */
              pk: filterOption.pk,
              text: filterOption.label,
              value: filterOption.slug,
            };
          }),
          label: filter.label,
          filterOptionsOrigin: filter.attributeOptionsOrigin, // TODO: RENAME attributeOptionsOrigin > filterOptionsOrigin
          slug: filter.slug,
        };
        if (!this.isCreationMode && !this.isSingleUser) {
          filterForForm.filterOptions.push({
            pk: null,
            text: this.indeterminateFilterOptionPlaceholderLabel,
            value: this.indeterminateFilterOptionPlaceholderSlug,
          });
        }
        filtersForForm.push(filterForForm);
      }

      return filtersForForm;
    },

    hasControl() {
      return hasEnrichedSlot(this, "control");
    },

    organizationSelectOptions() {
      if (!this.organizations) return [];
      return this.organizations.map((organization) => {
        return {
          text: organization.label,
          value: organization.pk.toString(),
        };
      });
    },
  },

  watch: {
    isCreationMode: {
      handler: function () {
        this.formModel = {
          addressFormFields: {},
          filterFormFields: {},
        };
      },
      immediate: true,
    },

    individuals: {
      handler: function (newIndividuals) {
        if (this.isCreationMode) return;

        if (newIndividuals && newIndividuals.length) {
          let isSingleUser = (this.isSingleUser = newIndividuals.length == 1);

          //let individualPks = null;
          let addressFormFields = {};
          let filterFormFields = {};

          if (isSingleUser) {
            let individual = newIndividuals[0];
            //individualPks = [individual.pk];
            for (const attrName of [
              "address",
              "postcode",
              "city",
              "longitude",
              "latitude",
            ])
              addressFormFields[attrName] = individual[attrName];

            for (const filter of this.filterTree.filters)
              filterFormFields[filter.slug] =
                  individual.attribute_tree[filter.slug] ||
                  this.gmbConstants.noFilterOptionSlug; // Initial values of select fields
          } else
            for (const filter of this.filterTree.filters) {
              let computedFilterOptionSlug;

              let filterOptionSlugs = new Set(
                  this.individuals.map(
                      (individual) =>
                          individual.attribute_tree[filter.slug] || "NO_VALUE"
                  )
              );
              if (filterOptionSlugs.size == 1)
                  // All individuals have the same value for that filter, even if it's "NO VALUE"
                [computedFilterOptionSlug] = filterOptionSlugs;
              else
                computedFilterOptionSlug =
                    this.indeterminateFilterOptionPlaceholderSlug;

              filterFormFields[filter.slug] = computedFilterOptionSlug;
            }

          this.formModel = {
            //individualPks: individualPks,
            //individualReferenceId: individualReferenceId,
            addressFormFields: addressFormFields,
            filterFormFields: filterFormFields,
          };
        } else this.formModel = null;
      },
      immediate: true,
    },
  },

  beforeCreate() {
  },

  created() {
  },

  beforeMount() {
  },

  mounted() {
  },

  beforeUpdate() {
  },

  updated() {
  },

  beforeUnmount() {
  },

  unmounted() {
  },

  methods: {
    closeModal() {
      ModalEventBus.$emit("gmb-close-modal", "create-individual");
    },

    createIndividual() {
      // FIXME DOESNT REFRESH SIDE-PANEL FILTERS PROPERLY - NEEDS FULL RELOAD OF SIDEPANEL
      let organizationId = parseInt(this.formModel.organization, 10);
      if (isNaN(organizationId)) {
        openToast(
            "Données invalides",
            "Veuillez sélectionner un site pour l'individu"
        );
        return;
      }

      let baseFields = this.gatherBaseIndividualFields(this.formModel);

      let addressFormFields = this.formModel.addressFormFields; // Must be filled

      let [filterPatchingData, _] = this.gatherFilterPatchingData(
          this.formModel.filterFormFields
      );

      let creationFields = Object.assign(
          {organization: organizationId},
          baseFields,
          addressFormFields,
          filterPatchingData
      );

      let promise = postApiEndpoint(`api/individuals/`, creationFields);
      promise.then((res) => {
        this.$emit("individuals-edited", []); // Reload ALL!
        openToast("Opération terminée", "L'individu a été créé");
        this.closeModal();
      });
    },

    deleteIndividuals(individuals) {
      if (
          confirm(
              `Voulez vous vraiment supprimer ${individuals.length} utilisateur(s) sélectionné(s) ?`
          )
      ) {
        let individualPks = individuals.map((individual) => individual.pk);
        postApiEndpoint(`/api/individuals/bulk-delete/`, {
          individual_ids: individualPks,
        }).then((resp) => {
          this.$emit("individuals-edited", []); // Hackish way of saying "reload all"
          openToast(
              "Opération terminée",
              "La suppression du/des individu(s) est effective"
          );
        });
      }
    },

    gatherFilterPatchingData(filterFormFields) {
      let indeterminateFilters = [];

      let filterPatchingData = {
        common_attribute_options: [],
        specific_attribute_options: [],
      };

      for (const [filterSlug, filterOptionSlug] of Object.entries(
          filterFormFields
      )) {
        let filterForForm = this.filtersForForm.find(
            (filter) => filter.slug == filterSlug
        );

        let existingEntry =
            filterPatchingData[filterForForm.filterOptionsOrigin];
        let isRealDbAttributeRecord =
            existingEntry && existingEntry instanceof Array;

        let filterOptionForForm = filterForForm.filterOptions.find(
            (filterOption) => filterOption.value == filterOptionSlug
        );

        if (
            filterOptionForForm.value ==
            this.indeterminateFilterOptionPlaceholderSlug // "Do not modify" status
        ) {
          indeterminateFilters.push(filterForForm);
          continue;
        }

        if (filterOptionForForm.pk === null) {
          // NO-VALUE option
          if (!isRealDbAttributeRecord) {
            filterPatchingData[filterForForm.filterOptionsOrigin] = ""; // Force empty STRING value for standalone DB choice fields
          }
          continue;
        }

        if (isRealDbAttributeRecord) {
          // case of a filter option to be added
          filterPatchingData[filterForForm.filterOptionsOrigin].push(
              filterOptionForForm.pk
          );
        } else {
          filterPatchingData[filterForForm.filterOptionsOrigin] =
              filterOptionForForm.pk; // Root field edition
        }
      }

      return [filterPatchingData, indeterminateFilters];
    },

    gatherBaseIndividualFields(formModel) {
      return {
        reference_id: formModel.reference_id || "", // FORCE non-null value because it's a REQUIRED field in Django
        individual_email: formModel.individual_email || "",
      };
    },

    onSubmit(event) {
      event.preventDefault();
      if (this.isCreationMode) this.createIndividual();
      else if (this.isSingleUser) this.updateIndividual();
      else this.updateIndividuals();
    },

    updateIndividual() {
      //let individualPk = this.formModel.individualPks[0];
      let individualPk = this.individuals[0].pk; // MUST exist
      let addressFormFields = this.formModel.addressFormFields; // Must be filled

      let [filterPatchingData, _] = this.gatherFilterPatchingData(
          this.formModel.filterFormFields
      );

      // Longitude/latitude could be sent as empty strings, but it's OK, they will properly be reset to None server-side
      let promise1 = postApiEndpoint(
          `api/individuals/${individualPk}/change-address/`,
          addressFormFields
      );

      // BEWARE - do not make requests in parallel, else data loss would occur server-side due to concurrent writes
      promise1.then((res) => {
        let promise2 = patchApiEndpoint(
            `api/individuals/${individualPk}/`,
            filterPatchingData
        );
        promise2.then((res) => {
          this.$emit("individuals-edited", [individualPk]);
          openToast("Opération terminée", "L'individu a été mis à jour");
        });
      }); // FIXME DOESNT REFRESH SIDE-PANEL FILTERS PROPERLY - NEEDS FULL RELOAD OF SIDEPANEL
    },

    updateIndividuals() {
      let bulkUpdatePayload = [];

      let [filterPatchingData, indeterminateFilters] =
          this.gatherFilterPatchingData(this.formModel.filterFormFields);

      for (let individual of this.individuals) {
        let customFilterPatchingData = structuredClone(filterPatchingData);

        for (let filter of indeterminateFilters) {
          let existingEntry =
              customFilterPatchingData[filter.filterOptionsOrigin];
          let isRealDbAttributeRecord =
              existingEntry && existingEntry instanceof Array;

          if (!isRealDbAttributeRecord) {
            // It's actually a model-level filter like transport_type, just skip it so that it doesn't get patched
            continue;
          }

          let currentFilterOptionSlug = individual.attribute_tree[filter.slug];
          if (currentFilterOptionSlug) {
            // Individual had a value set for this filter so we must keep it in the array
            let currentFilterOptionPk = filter.filterOptions.find(
                (filterOption) => filterOption.value == currentFilterOptionSlug
            ).pk;
            customFilterPatchingData[filter.filterOptionsOrigin].push(
                currentFilterOptionPk
            );
          }
        }

        bulkUpdatePayload.push({
          pk: individual.pk,
          ...customFilterPatchingData,
        });
      }

      let promise = patchApiEndpoint(
          "/api/individuals/bulk-update/",
          bulkUpdatePayload
      );
      promise.then((res) => {
        this.$emit(
            "individuals-edited",
            bulkUpdatePayload.map((obj) => obj.pk)
        );
        openToast("Opération terminée", "Les individus ont été mis à jour");
      });
    },
  },
};
</script>

<style lang="scss"></style>
